import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { setProject } from "../redux/authSlice";
import {
  LayoutDashboard,
  Bug,
  FileText,
  Play,
  Code,
  Eye,
  BarChart,
  Settings,
  Bell,
  Search,
  Sun,
  Moon,
  LogOut,
  CheckCircle,
  GitBranch,
  Users,
  AlertTriangle,
  FileCheck,
  ClipboardList,
  Briefcase,
  Plus,
  ChevronRight,
  Sparkles,
  ArrowLeft,
  Edit3,
  Filter,
  Calendar,
  User,
  Upload,
  X,
  Info,
  Zap,
  Scale,
  TrendingUp,
  Loader2,
} from "lucide-react";
import {
  BarChart as ReBar,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";
import StatCard from "../components/Cards/StatCard";
import BugItem from "../components/Cards/BugItem";
import ActionCard from "../components/Cards/ActionCard";

export default function Dashboard({ darkMode }) {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const dispatch = useDispatch();
  const { currentUser, selectedProject } = useSelector((state) => state.auth);

  const [project, setProjectLocal] = useState(selectedProject);
  const [recentBugs, setRecentBugs] = useState([]);
  const [recentTestCases, setRecentTestCases] = useState([]);
  const [chartData, setChartData] = useState([]);

  // Stats state
  const [stats, setStats] = useState({
    activeTests: 0,
    openBugs: 0,
    testCoverage: 0,
    releaseScore: 0, // Default to 0 instead of 100
  });

  const [isLoading, setIsLoading] = useState(true);
  const borderColor = darkMode ? "border-gray-800/40" : "border-gray-200";
  const cardBg = darkMode ? "bg-[#161922]" : "bg-white";

  // Project Initialization
  useEffect(() => {
    if (
      selectedProject &&
      projectId &&
      Number(projectId) === Number(selectedProject.project_id)
    ) {
      setProjectLocal(selectedProject);
      return;
    }

    if (projectId) {
      const fetchProject = async () => {
        try {
          const res = await axios.get(
            `https://api.testoraglobal.in/api/project/id/${projectId}`,
          );
          if (res.data) {
            setProjectLocal(res.data);
            dispatch(setProject(res.data));
          }
        } catch (error) {
          setProjectLocal(null);
        }
      };
      fetchProject();
    } else {
      setProjectLocal(selectedProject);
    }
  }, [projectId, selectedProject, dispatch]);

  // Fetch Dashboard Data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const bugsUrl = project?.project_id
          ? `https://api.testoraglobal.in/api/bug/project/${project.project_id}`
          : "https://api.testoraglobal.in/api/bug/all";

        const tcUrl = project?.project_id
          ? `https://api.testoraglobal.in/api/testcases/project/${project.project_id}`
          : "https://api.testoraglobal.in/api/testcases";

        // Fetch the execution history to count runs instead of overwrites
        const historyUrl = project?.project_id
          ? `https://api.testoraglobal.in/api/history/${project.project_id}`
          : null;

        const [bugsRes, tcRes, historyRes] = await Promise.all([
          axios.get(bugsUrl).catch(() => ({ data: [] })),
          axios.get(tcUrl).catch(() => ({ data: [] })),
          historyUrl ? axios.get(historyUrl).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        ]);

        const bugs = Array.isArray(bugsRes.data) ? bugsRes.data : [];
        const tcs = Array.isArray(tcRes.data) ? tcRes.data : [];
        const history = Array.isArray(historyRes.data) ? historyRes.data : [];

        // --- 1. CALCULATE TOP STATS (Still uses Test Cases state) ---
        const activeTests = tcs.filter(
          (tc) =>
            tc.status === "Active" || tc.status === "Active(Not Executed)",
        ).length;
        const passedTests = tcs.filter((tc) => tc.status === "Pass").length;
        const failedTests = tcs.filter((tc) => tc.status === "Fail").length;
        const totalTests = tcs.length;

        const coverage =
          totalTests > 0
            ? Math.round(((passedTests + failedTests) / totalTests) * 100)
            : 0;

        // Open bugs (excluding closed/resolved)
        const openBugsList = bugs.filter((bug) => {
          const s = bug.status?.toLowerCase() || "";
          return (
            !s.includes("close") &&
            !s.includes("resolve") &&
            !s.includes("done")
          );
        });

        // Tally bugs by severity for the math
        let criticalBugs = 0, highBugs = 0, mediumBugs = 0, lowBugs = 0;
        openBugsList.forEach((bug) => {
          const sev = (bug.severity || bug.status || "").toLowerCase();
          if (sev.includes("critical")) criticalBugs++;
          else if (sev.includes("high")) highBugs++;
          else if (sev.includes("low")) lowBugs++;
          else mediumBugs++;
        });

        // --- MATCHED RELEASE SCORE CALCULATION (60/40 Split) ---
        let riskScore = 0;
        if (totalTests > 0) {
          const testProgressScore = (passedTests / totalTests) * 60;
          
          let defectHealthScore = 40;
          defectHealthScore -= (criticalBugs * 2.0);
          defectHealthScore -= (highBugs * 1.5);
          defectHealthScore -= (mediumBugs * 0.5);
          defectHealthScore -= (lowBugs * 0.1);
          
          defectHealthScore = Math.max(0, defectHealthScore);
          riskScore = Math.round(testProgressScore + defectHealthScore);
        } else {
          // 👉 CHANGED: If no test cases are declared, score is strictly 0.
          riskScore = 0;
        }

        setStats({
          activeTests,
          openBugs: openBugsList.length,
          testCoverage: coverage,
          releaseScore: riskScore,
        });

        // --- 2. MULTI-BAR CHART DATA: (Based on TRUE Execution Count from History) ---
        const today = new Date();
        const runsPerDay = {};

        // Generate last 10 days structure
        const last10Days = Array.from({ length: 10 }).map((_, i) => {
          const d = new Date();
          d.setDate(today.getDate() - (9 - i));

          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");

          const dateKey = `${year}-${month}-${day}`;
          runsPerDay[dateKey] = { passed: 0, failed: 0 };
          return { dateKey, label: `${month}/${day}` };
        });

        // ITERATE THROUGH HISTORY INSTEAD OF TEST CASES
        history.forEach((run) => {
          const isPass = run.status === "Pass";
          const isFail = run.status === "Fail";

          if (isPass || isFail) {
            const dateString = run.created_at || run.createdAt;
            let runDateKey = null;

            if (dateString) {
              const d = new Date(dateString);
              if (!isNaN(d.getTime())) {
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, "0");
                const day = String(d.getDate()).padStart(2, "0");
                runDateKey = `${year}-${month}-${day}`;
              }
            }

            if (runDateKey && runsPerDay[runDateKey]) {
              if (isPass) runsPerDay[runDateKey].passed++;
              if (isFail) runsPerDay[runDateKey].failed++;
            }
          }
        });

        const mappedChartData = last10Days.map((item) => ({
          name: item.label,
          passed: runsPerDay[item.dateKey].passed,
          failed: runsPerDay[item.dateKey].failed,
        }));

        setChartData(mappedChartData);

        // --- 3. RECENT TEST CASES ---
        const sortedTCs = [...tcs].sort((a, b) => {
          return (
            new Date(b.created_date || 0).getTime() -
            new Date(a.created_date || 0).getTime()
          );
        });
        setRecentTestCases(sortedTCs.slice(0, 4));

        // --- 4. RECENT BUGS ---
        const sortedBugs = [...bugs].sort((a, b) => {
          return (
            new Date(b.created_date || 0).getTime() -
            new Date(a.created_date || 0).getTime()
          );
        });
        setRecentBugs(sortedBugs.slice(0, 4));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [project, darkMode]);

  // Check if we have any chart data at all
  const totalExecutions = chartData.reduce((sum, item) => sum + item.passed + item.failed, 0);

  return (
    <div className="px-8 pb-8 max-w-7xl mx-auto animate-in fade-in duration-500">
      <h2 className="text-3xl font-bold tracking-tight">
        {project ? "Project Dashboard" : "Dashboard"}
      </h2>
      <p className="text-gray-500 text-sm mt-1 mb-8">
        {project
          ? `Overview for ${project.project_name}`
          : "Welcome back! Please select a project to view its dashboard."}
      </p>

      {/* TOP STATS */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
        <StatCard
          title="Active Tests"
          value={stats.activeTests.toString()}
          trend="Needs Run"
          icon={<Play size={18} />}
          color="blue"
          darkMode={darkMode}
        />
        <StatCard
          title="Open Bugs"
          value={stats.openBugs.toString()}
          trend="Pending Fix"
          icon={<Bug size={18} />}
          color="red"
          darkMode={darkMode}
        />
        <StatCard
          title="Test Coverage"
          value={`${stats.testCoverage}%`}
          trend={stats.testCoverage > 80 ? "Good" : "Needs Work"}
          icon={<CheckCircle size={18} />}
          color="green"
          darkMode={darkMode}
        />
        <StatCard
          title="Release Readiness"
          value={`${stats.releaseScore}/100`}
          trend={stats.releaseScore === 0 && stats.activeTests === 0 ? "Not Started" : stats.releaseScore > 80 ? "Ready" : "At Risk"}
          icon={<AlertTriangle size={18} />}
          color={
            stats.releaseScore === 0 && stats.activeTests === 0
              ? "gray"
              : stats.releaseScore > 80
                ? "green"
                : stats.releaseScore > 50
                  ? "orange"
                  : "red"
          }
          darkMode={darkMode}
        />
      </div>

      {/* FULL WIDTH DUAL-BAR CHART */}
      <div className="grid grid-cols-1 gap-6 mb-8">
        <div
          className={`p-6 rounded-2xl border transition-colors relative ${cardBg} ${borderColor}`}
        >
          <h3 className="font-bold text-sm mb-8">
            Test Executions (Last 10 Days)
          </h3>

          <div
            style={{ height: "300px", minHeight: "300px", width: "100%" }}
            className="relative"
          >
            {isLoading ? (
              <div className="flex items-center justify-center h-full text-gray-500">
                <Loader2 className="animate-spin mr-2" size={20} /> Loading
                Chart Data...
              </div>
            ) : (
              <>
                {totalExecutions === 0 && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-white/50 dark:bg-[#161922]/50 backdrop-blur-[1px] rounded-xl">
                    <BarChart
                      size={32}
                      className="text-gray-400 mb-2 opacity-50"
                    />
                    <p className="text-gray-500 font-medium">
                      No tests executed in the last 10 days.
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Change a test case status to Pass/Fail to see metrics.
                    </p>
                  </div>
                )}

                <ResponsiveContainer width="100%" height={300} minHeight={300}>
                  <ReBar
                    data={chartData}
                    margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                  >
                    <XAxis
                      dataKey="name"
                      stroke="#4b5563"
                      fontSize={11}
                      tickLine={false}
                      axisLine={true}
                      dy={10}
                    />
                    <YAxis
                      stroke="#4b5563"
                      fontSize={11}
                      tickLine={false}
                      axisLine={true}
                      allowDecimals={false}
                      domain={[0, (dataMax) => (dataMax === 0 ? 5 : dataMax)]}
                    />
                    <CartesianGrid
                      strokeDasharray="3 3"
                      vertical={false}
                      stroke={darkMode ? "#1f2937" : "#e5e7eb"}
                      opacity={0.5}
                    />
                    <Tooltip
                      cursor={{ fill: "transparent" }}
                      contentStyle={{
                        backgroundColor: darkMode ? "#161922" : "#ffffff",
                        border: `1px solid ${darkMode ? "#374151" : "#e5e7eb"}`,
                        borderRadius: "8px",
                        color: darkMode ? "#fff" : "#000",
                      }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: 10 }} />
                    <Bar
                      dataKey="passed"
                      name="Passed"
                      fill="#10b981"
                      radius={[4, 4, 0, 0]}
                      barSize={16}
                    />
                    <Bar
                      dataKey="failed"
                      name="Failed"
                      fill="#ef4444"
                      radius={[4, 4, 0, 0]}
                      barSize={16}
                    />
                  </ReBar>
                </ResponsiveContainer>
              </>
            )}
          </div>
        </div>
      </div>

      {/* RECENT LISTS */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8 items-stretch">
        
        {/* RECENT TEST CASES */}
        <div
          className={`p-6 rounded-2xl border transition-colors flex flex-col h-full ${cardBg} ${borderColor} hover:shadow-lg`}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-sm">Recently Created Test Cases</h3>
            <span
              onClick={() => navigate("/test-cases")}
              className="text-purple-500 text-xs font-medium cursor-pointer flex items-center gap-1"
            >
              View all <Play size={10} fill="currentColor" />
            </span>
          </div>
          <div className="space-y-2 flex-1 flex flex-col justify-start">
            {isLoading ? (
              <div className="text-sm text-gray-500 py-8 flex-1 flex items-center justify-center">
                Loading test cases...
              </div>
            ) : recentTestCases.length === 0 ? (
              <div className="text-sm text-gray-500 py-8 flex-1 flex items-center justify-center">
                No test cases found.
              </div>
            ) : (
              recentTestCases.map((tc) => (
                <div
                  key={tc.internal_id || tc.test_id}
                  onClick={() =>
                    navigate(`/test-cases/${tc.internal_id || tc.test_id}`)
                  }
                  className={`cursor-pointer transition-all hover:opacity-80 p-3 -mx-3 rounded-xl flex items-center justify-between ${
                    darkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"
                  }`}
                >
                  <div className="flex flex-col gap-1 overflow-hidden pr-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] font-black text-indigo-500 bg-indigo-500/10 px-2 py-0.5 rounded uppercase tracking-widest shrink-0">
                        {tc.test_id || tc.internal_id || "TC-0000"}
                      </span>
                      <span className="text-sm font-semibold truncate dark:text-gray-200">
                        {tc.title || tc["test-case-title"] || "Untitled Test Case"}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400 pl-1">
                      Category: {tc.category || "N/A"} •{" "}
                      {new Date(tc.created_date || new Date()).toLocaleDateString()}
                    </span>
                  </div>

                  <div className="shrink-0">
                    <span
                      className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                        tc.status?.toLowerCase() === "pass"
                          ? "bg-green-500/10 text-green-600 dark:text-green-500"
                          : tc.status?.toLowerCase() === "fail"
                            ? "bg-red-500/10 text-red-600 dark:text-red-500"
                            : "bg-blue-500/10 text-blue-600 dark:text-blue-500"
                      }`}
                    >
                      {tc.status || "Active"}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* RECENT BUGS */}
        <div
          className={`p-6 rounded-2xl border transition-colors flex flex-col h-full ${cardBg} ${borderColor} hover:shadow-lg`}
        >
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-bold text-sm">Recent Bugs</h3>
            <span
              onClick={() => navigate("/bug-optimizer")}
              className="text-purple-500 text-xs font-medium cursor-pointer flex items-center gap-1"
            >
              View all <Play size={10} fill="currentColor" />
            </span>
          </div>
          <div className="space-y-2 flex-1 flex flex-col justify-start">
            {isLoading ? (
              <div className="text-sm text-gray-500 py-8 flex-1 flex items-center justify-center">
                Loading recent bugs...
              </div>
            ) : recentBugs.length === 0 ? (
              <div className="text-sm text-gray-500 py-8 flex-1 flex items-center justify-center">
                No recent bugs found.
              </div>
            ) : (
              recentBugs.map((bug) => {
                const bugSeverity = (bug.severity || bug.status || "Medium").toLowerCase();
                return (
                  <div
                    key={bug.bug_id || bug.id}
                    onClick={() =>
                      navigate(`/bug-optimizer/view/${bug.bug_id || bug.id}`)
                    }
                    className={`cursor-pointer transition-all hover:opacity-80 p-3 -mx-3 rounded-xl flex items-center justify-between ${
                      darkMode ? "hover:bg-gray-800/40" : "hover:bg-gray-50"
                    }`}
                  >
                    <div className="flex flex-col gap-1 overflow-hidden pr-2">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-black text-red-500 bg-red-500/10 px-2 py-0.5 rounded uppercase tracking-widest shrink-0">
                          {bug.bug_id || bug.id || "BUG-0000"}
                        </span>
                        <span className="text-sm font-semibold truncate dark:text-gray-200">
                          {bug.title || "Untitled Bug"}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500 dark:text-gray-400 pl-1">
                        Assignee: {bug.assigned_to || bug.raised_by_name || bug.raised_by || "Unassigned"} •{" "}
                        {new Date(bug.created_date || new Date()).toLocaleDateString()}
                      </span>
                    </div>

                    <div className="shrink-0">
                      <span
                        className={`text-[10px] font-bold uppercase px-2 py-1 rounded-full ${
                          bugSeverity.includes("critical") || bugSeverity.includes("high")
                            ? "bg-red-500/10 text-red-600 dark:text-red-500"
                            : bugSeverity.includes("medium")
                              ? "bg-orange-500/10 text-orange-600 dark:text-orange-500"
                              : "bg-blue-500/10 text-blue-600 dark:text-blue-500"
                        }`}
                      >
                        {bug.severity || bug.status || "Medium"}
                      </span>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* QUICK ACTIONS */}
      <div
        className={`p-6 rounded-2xl border transition-colors ${cardBg} ${borderColor}`}
      >
        <h3 className="font-bold text-sm mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActionCard
            title="Run New Test"
            desc="Execute autonomous tests"
            icon={<Play size={20} />}
            darkMode={darkMode}
            onClick={() => navigate("/test-executor")}
          />
          <ActionCard
            title="Report Bug"
            desc="AI-optimized bug reports"
            icon={<Bug size={20} />}
            darkMode={darkMode}
            onClick={() => navigate("/bug-optimizer/createbug")}
          />
          <ActionCard
            title="Map Requirements"
            desc="Generate test cases"
            icon={<CheckCircle size={20} />}
            darkMode={darkMode}
            onClick={() => navigate("/requirement-mapper")}
          />
        </div>
      </div>
    </div>
  );
}